import {type ActionTree} from 'vuex';
import {getAvatarByName} from '../../../filters/username';
import {orderBy} from '../../../library/filter';
import LocalStorage from '../../../library/localStorage';
import XHRManager from '../../../library/xhrManager';
import {deepCopyObject} from '../../../utils/portuna';
import store from '../../index';
import {telephoneStatuses} from '@/library/modules/employee/employee';

type Notification = {
    title: string
    options: NotificationOptions
};

let initialized = false;

const actions: ActionTree<TelephoneToolbarState, RootState> = {
    updateFromStorage({commit}, data)
    {
        commit('setLastUpdate', data.lastUpdate);
        commit('setSettings', data.settings);
        commit('setEmployees', data.employees);
        commit('setOpenRequestCount', data.openRequestCount);
    },
    handleChanges({commit, rootGetters, state}, data)
    {
        let employees                             = {};
        let settings                              = data.settings || state.settings;
        const openRequestCount = data.openRequestCount || state.openRequestCount;
        let ownEntities: string[]                 = [];
        let changedData: Record<string, string[]> = {};

        if (settings.onlyEntity)
        {
            for (let key in data.employees)
            {
                if (!data.employees.hasOwnProperty(key) || data.employees[key].id !== rootGetters.employeeId)
                {
                    continue;
                }

                ownEntities = Object.keys(data.employees[key].entities);
                break;
            }
        }

        for (let key in data.employees)
        {
            if (!data.employees.hasOwnProperty(key))
            {
                continue;
            }

            let employee   = data.employees[key];
            let employeeId = employee.id;

            employees[employeeId] = employee;

            if (!telephoneStatuses[employee.status])
            {
                employee.status = 'default';
            }

            if (parseInt(employeeId) !== rootGetters.employeeId
                && state.employees[employeeId]
                && state.employees[employeeId].status !== employee.status)
            {
                if (settings.onlyEntity)
                {
                    let skip = true;
                    for (let id of ownEntities)
                    {
                        if (id in employee.entities)
                        {
                            skip = false;
                            break;
                        }
                    }
                    if (skip)
                    {
                        continue;
                    }
                }

                let status = employee.status;
                if (typeof telephoneStatuses[status].groupWith !== 'undefined')
                {
                    status = telephoneStatuses[status].groupWith;
                }

                let action = '';
                switch (String(status))
                {
                    case '1':
                        action = 'krank';
                        break;
                    case '0':
                        if (3 === parseInt(state.employees[employeeId].status))
                        {
                            action = 'jetzt wieder anwesend';
                            break;
                        }
                        action = 'jetzt anwesend';
                        break;
                    case '3':
                        action = 'jetzt in der Pause';
                        break;
                    case 'default':
                        if([0, 3].includes(parseInt(state.employees[employeeId].status)))
                        {
                            action = 'jetzt abwesend';
                        }
                        break;
                }

                if(!action.length)
                {
                    continue;
                }

                if ('undefined' === typeof changedData[action])
                {
                    changedData[action] = [];
                }
                changedData[action].push(employeeId);
            }
        }

        commit('setSettings', settings);
        commit('setEmployees', employees);
        commit('setOpenRequestCount', openRequestCount);
        commit('setInitialised', true);
        LocalStorage.set('telephoneToolbar', {
            lastUpdate: Date.now(),
            settings,
            employees,
            openRequestCount,

        });

        // no notification wanted
        if (!settings.showNotifications)
        {
            return;
        }

        // browser does not support notifications - stop send notification
        if (!('Notification' in window) || Notification.permission !== 'granted')
        {
            return;
        }

        // check if a notification required
        if (!Object.keys(changedData).length)
        {
            return;
        }

        // build notification title and options
        let notifications: Notification[] = [];
        for (let action in changedData)
        {
            if(!changedData.hasOwnProperty(action))
            {
                continue;
            }

            let notificationUsers = [];
            for (let employeeId of changedData[action])
            {
                notificationUsers.push({
                    name: employees[employeeId].full_name,
                    surname: employees[employeeId].surname,
                    systemId: employees[employeeId].system_id,
                    image: employees[employeeId].path,
                    telephone: employees[employeeId].telephone,
                });
            }

            // sort users by surname
            notificationUsers = orderBy(notificationUsers, 'surname');

            // send notifications in multiple parts (prevent cutting names)
            while (notificationUsers.length)
            {
                let users = notificationUsers.splice(0, 6);
                // multiple users (no icon)
                if (users.length > 1)
                {
                    let ids   = [],
                        names = [];
                    for (let user of users)
                    {
                        ids.push(user.systemId);
                        names.push(user.name + (user.telephone ? ' (Telefon: ' + user.telephone + ')' : ''));
                    }

                    notifications.push({
                        title: users.length + ' Mitarbeiter sind ' + action + ':',
                        options: {
                            tag: 'toolbar-multiple-notification-' + ids.join('-'),
                            body: names.join(', '),
                        },
                    });
                }
                // single user (add icon to notification)
                else
                {
                    let user = users[0];
                    notifications.push({
                        title: user.name,
                        options: {
                            tag: 'toolbar-single-notification-' + user.systemId,
                            body: 'ist ' + action + '.' + (user.telephone ? '\nTelefon: ' + user.telephone : ''),
                            icon: user.image ? user.image : getAvatarByName(user.name, 'circle', true, 150),
                        },
                    });
                }
            }
        }

        // send notification to user
        const sendNotification = () =>
        {
            let data = notifications.shift();
            if (data)
            {
                let notification     = new window.Notification(data.title, data.options);
                notification.onclose = sendNotification;
            }
        };
        sendNotification();
    },
    refetchTelephoneData({dispatch})
    {
        const xhrCallback: XHRCallback = (err, data) =>
        {
            if (err)
            {
                return;
            }

            dispatch('handleChanges', data);
        };
        XHRManager.sendCallback('telephoneToolbar', xhrCallback);
    },

    fetch({dispatch, state})
    {
        const xhrCallback: XHRCallback = (err, data) =>
        {
            if (err)
            {
                return;
            }

            dispatch('handleChanges', data);
        };

        // if not initialized create an event for local storage update on telephone toolbar key
        if (!initialized)
        {
            initialized = true;
            LocalStorage.on('telephoneToolbar', (event) =>
            {
                if(event.newValue && typeof event.newValue === 'object' && event.newValue.employees)
                {
                    dispatch('updateFromStorage', event.newValue);
                }
            });

            XHRManager.add(30,'telephoneToolbar', xhrCallback);
        }

        if (state.lastUpdate === 0)
        {
            let localStorageData = LocalStorage.get('telephoneToolbar');
            if (localStorageData !== null && Date.now() - localStorageData.lastUpdate > 60 * 1000)
            {
                localStorageData = null;
            }

            if (localStorageData === null)
            {
                XHRManager.sendCallback('telephoneToolbar', xhrCallback);
                return;
            }

            dispatch('updateFromStorage', localStorageData);
        }
    },
    open({commit})
    {
        commit('setOpen', true);
    },
    close({commit})
    {
        commit('setOpen', false);
    },
    setOpenState({commit}, open)
    {
        commit('setOpen', open);
    },
    updateEmployees({
        commit,
        dispatch,
        state,
    }, data: EmployeeTelephoneListWebSocketEntryMultiple)
    {
        let currentEmployees = deepCopyObject(state.employees);

        for (let employeeKey in currentEmployees)
        {
            if (!currentEmployees.hasOwnProperty(employeeKey))
            {
                continue;
            }

            for (let payloadEmployeeKey in data.employees)
            {
                if (!data.employees.hasOwnProperty(payloadEmployeeKey))
                {
                    continue;
                }

                const payloadEmployee = data.employees[payloadEmployeeKey];

                if (currentEmployees[employeeKey].id === payloadEmployee.id)
                {
                    for (let employeeDataKey in payloadEmployee)
                    {
                        if (
                            !payloadEmployee.hasOwnProperty(employeeDataKey)
                            || ['userData', 'employeeData'].includes(employeeDataKey)
                        )
                        {
                            continue;
                        }

                        currentEmployees[employeeKey][employeeDataKey] = payloadEmployee[employeeDataKey];
                    }

                    store
                    .commit(
                        'personSet',
                        {
                            type: 'users',
                            id: payloadEmployee.system_id,
                            data: payloadEmployee.userData,
                        },
                    );
                    store
                    .commit(
                        'personSet',
                        {
                            type: 'employees',
                            id: payloadEmployee.id,
                            data: payloadEmployee.employeeData,
                        },
                    );
                }
            }
        }

        dispatch('handleChanges', {employees: currentEmployees});
    },
    getBadge({state}, {
        id,
        type,
    }: { id: number; type: 'user' | 'employee'; })
    {
        for (const employee in state.employees)
        {
            if (!state.employees.hasOwnProperty(employee))
            {
                continue;
            }

            if (
                'employee' === type && id === state.employees[employee].id
                || 'user' === type && id === state.employees[employee].system_id
            )
            {
                return state.employees[employee].badge;
            }
        }

        return false;
    },
};

export default actions;